import { ReadOnlyAdminUsernames, UserSchema } from "../types/auth.type";
import type { AuthTokenPayload } from "../auth/types";
import { ADMIN_READ_ONLY_ROLE } from "../auth/legacy-roles";
import { getLegacyRolesForOrg, isPlatformReadOnly } from "../auth/jwt";

/** Mirrors sidebar read-only detection in Layout (username or email). */
export function isReadOnlyAdminUser(
  user: UserSchema | null | undefined,
): boolean {
  const readOnly = Object.values(ReadOnlyAdminUsernames).map((x) =>
    String(x).toLowerCase(),
  );
  const username = user?.username?.toLowerCase();
  const email = user?.email?.toLowerCase();
  return (
    (!!username && readOnly.includes(username)) ||
    (!!email && readOnly.includes(email))
  );
}

/** True when JWT legacy roles include admin_read_only for any org. */
export function isReadOnlyAdminFromPayload(
  payload: AuthTokenPayload | null | undefined,
): boolean {
  if (!payload) {
    return false;
  }
  const raw = payload.permissions;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return false;
  }
  for (const roles of Object.values(raw)) {
    if (Array.isArray(roles) && roles.includes(ADMIN_READ_ONLY_ROLE)) {
      return true;
    }
  }
  return false;
}

export function isReadOnlyAdmin(
  user: UserSchema | null | undefined,
  payload: AuthTokenPayload | null | undefined,
): boolean {
  return (
    isReadOnlyAdminUser(user) ||
    isReadOnlyAdminFromPayload(payload) ||
    isPlatformReadOnly(payload)
  );
}

export function canMutate(
  user: UserSchema | null | undefined,
  payload: AuthTokenPayload | null | undefined,
  canAccessFn: (required: string, legacyRoles?: readonly string[]) => boolean,
  required: string,
  legacyRoles?: readonly string[],
): boolean {
  if (isReadOnlyAdmin(user, payload)) {
    return false;
  }
  return canAccessFn(required, legacyRoles);
}

/** Check if user has any org/platform access after login. */
export function hasAnyAdminAccess(
  payload: AuthTokenPayload | null | undefined,
  isPlatformAdminFlag: boolean,
  permissionList: readonly string[] = [],
): boolean {
  if (isPlatformAdminFlag) {
    return true;
  }
  if ((payload?.platform_permissions?.length ?? 0) > 0) {
    return true;
  }
  if (permissionList.length > 0) {
    return true;
  }
  if (!payload) {
    return false;
  }
  const raw = payload.permissions;
  if (Array.isArray(raw) && raw.length > 0) {
    return true;
  }
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    return Object.keys(raw).length > 0;
  }
  return false;
}

export { getLegacyRolesForOrg };
